import i18n from 'i18next'
import { uniqueId } from 'lodash-es'
import { DataItem } from 'scrivito'

import {
  FilterStructure,
  SavedFilterStructure,
} from '@/Atomic/Organisms/FilterBuilder'
import { getFilterDescription } from '@/Atomic/Organisms/FilterBuilder/utils/filterToReadable'
import { SchemaAttributes, SchemaRelation } from '@/Data/types'
import { askAi } from '@/utils/ai/aiBackendConnector'

/**
 * Same model id as `getFilterDescription`'s `JrModel.Fast`, kept as a local
 * literal so this molecule doesn't depend on the AiChatBot widget's internal
 * config — `core/` is meant to stay host-/consumer-neutral, not a dependency
 * surface for unrelated features.
 */
const FAST_MODEL_ID = 'jr-auto/fast'

const getSavedFilter = (
  dataItem: DataItem,
): SavedFilterStructure | undefined => {
  const query = dataItem.get('query') as Record<string, unknown> | undefined
  return query?.filters as SavedFilterStructure | undefined
}

/** Whether the list has filter data a description could meaningfully be generated from. */
export const hasFilterData = (dataItem: DataItem): boolean =>
  Boolean(getSavedFilter(dataItem)?.or?.length)

/** Converts the persisted filter format into the runtime shape `filterToReadable` expects. */
const toRuntimeFilterStructure = (
  saved: SavedFilterStructure,
  entityName: string,
): FilterStructure => ({
  groups: saved.or.map((group) => ({
    id: uniqueId('group_'),
    conditions: group.and.map((cond) => ({
      id: uniqueId('cond_'),
      field: cond.field,
      entity: entityName,
      operator: cond.op,
      value: cond.value,
    })),
  })),
})

/**
 * Generates a fresh description from the list's own filter data.
 * Returns an empty string if there is no filter data or the AI call fails.
 */
export const generateDescriptionText = async (
  dataItem: DataItem,
  entityName: string,
  baseSchemaAttributes?: SchemaAttributes,
  schemaRelations?: SchemaRelation[],
): Promise<string> => {
  const savedFilter = getSavedFilter(dataItem)
  if (!savedFilter?.or?.length) return ''

  const filterStructure = toRuntimeFilterStructure(savedFilter, entityName)
  const raw = await getFilterDescription(
    filterStructure,
    entityName,
    baseSchemaAttributes,
    schemaRelations,
  )
  if (!raw) return ''

  const json = raw
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '')
    .trim()

  try {
    const parsed = JSON.parse(json) as Record<string, string>
    const lang = i18n.language || 'en'
    return parsed[lang] ?? parsed[Object.keys(parsed)[0] ?? ''] ?? raw
  } catch {
    return raw
  }
}

/**
 * Improves a user-written (or previously generated) description, preserving
 * its intent and substance. Returns an empty string if the AI call fails.
 */
export const reviewDescriptionText = (currentText: string): Promise<string> => {
  const prompt = [
    'You are given a short description of a CRM list/segment, written by a',
    'user. Improve it: fix grammar and clarity, and make it more concise and',
    "natural, while strictly preserving the user's intent and substance.",
    'Reply in the same language as the input text.',
    '',
    'Respond with ONLY the improved text — no quotes, no markdown, no',
    'commentary.',
    '',
    'Text:',
    currentText,
  ].join('\n')

  return askAi([{ role: 'user', content: prompt }], FAST_MODEL_ID)
}
